import { useEffect, useRef, useState } from 'react';

import {
  subscriptionApi,
  type SubscriptionChangePreview,
} from '@repo/subscription/api';
import { fireToast } from '@repo/ui/components/Toast';
import isEqual from 'lodash/isEqual';

import { SubscriptionChangePreviewData, SubscriptionHookParams } from './types';
import useBillingPrices from './useBillingPrices';
import useSubscriptionChangeState from './useSubscriptionChangeState';

/**
 * Responsible for fetching the subscription change preview data, essentially the adjusted price to be paid for a new subscription when a user has an exisiting subscription.
 * It also manages the loading state, missing payment method flag, and whether the current subscription is past due or not.
 */
const useSubscriptionPreview = ({
  currentSubscription,
}: SubscriptionHookParams): SubscriptionChangePreviewData => {
  const pricesRef = useRef<ReturnType<typeof useBillingPrices> | null>(null);

  const [missingPaymentMethod, setMissingPaymentMethod] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [preview, setPreview] = useState<SubscriptionChangePreview | null>(
    null
  );

  const pricesToChange = useBillingPrices();
  const isPlanChanged = !!useSubscriptionChangeState({ currentSubscription });
  const isPastDue = Boolean(currentSubscription?.pastDue);

  useEffect(() => {
    if (isEqual(pricesToChange, pricesRef.current)) {
      return;
    }

    pricesRef.current = pricesToChange;

    const loadPreview = async (): Promise<void> => {
      setIsLoading(true);

      if (!pricesToChange) {
        setIsLoading(false);

        return;
      }

      const response = await subscriptionApi.previewSubscriptionChange({
        localPrices: pricesToChange,
      });

      if (pricesRef.current !== pricesToChange) {
        return;
      }

      setIsLoading(false);

      if ('error' in response) {
        if (
          response.error === 'PAYMENT_METHOD_MISSING' ||
          response.error === 'PAYMENT_IS_PAST_DUE' ||
          response.error === 'SUBSCRIPTION_IS_PAST_DUE'
        ) {
          setMissingPaymentMethod(true);

          return;
        }
        fireToast({
          label: `Failed to load data to preview the subscription change. ${response.error}`,
          error: true,
          duration: 5000,
        });
        return;
      }

      setPreview(response.subscription);
    };

    if (isPlanChanged) {
      void loadPreview();
    } else {
      setPreview(null);
      setIsLoading(false);
    }
  }, [currentSubscription, isPlanChanged, pricesToChange]);

  return {
    isLoading,
    preview,
    missingPaymentMethod,
    isPastDue,
  };
};

export default useSubscriptionPreview;
